import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PortfolioInboxes } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import { IndeterminateCheckboxState } from "@/Common/Models/UI/Inputs/IndeterminateCheckboxState";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { SentFaxSearchSettings } from "@/Portfolio/Models/Settings/SentFaxSearchSettings";
import { ISentFaxInboxModel } from "@/Portfolio/Models/UI/ISentFaxInboxModel";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class MySentFaxSettingsContainerHandler extends BaseOperationHandler {
    private helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ClinicianFilterTextChanged, this.clinicianFilterChanged);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ShowOptionsButtonClick, this.displayMySentFaxSettings);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_FromDateChanged, this.fromDateChanged);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ToDateChanged, this.toDateChanged);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_OkClick, this.onOkClick);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_CancelClick, this.onCancelClick);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange, this.onSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ColumnOrderingPopup_Open, this.showColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ColumnOrderingPopup_Close, this.closeColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.closeSearchFilterPopup, false);
    }

    private clinicianFilterChanged(request: LocalEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();

        model = model
            .set("CliniciansSearchText", request.Get<string>("value"));

        model = model
            .set("CliniciansFiltered", store.getMySentFaxCliniciansBasedOnSitesAndQuery(model));

        store.setActiveFaxModel(model);
    }

    private displayMySentFaxSettings(request: LocalEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();
        let open = model.get("ShowMySentFaxDisplaySettings");
        model = model.set("ShowMySentFaxDisplaySettings", !open);
        store.setActiveFaxModel(model);
    }

    private fromDateChanged(request: LocalEvent, store: PortfolioStore): void {
        let dateFrom = request.Get<Date>("value");
        dateFrom = dateFrom && moment(dateFrom).startOf("day").toDate();

        let model = store.getActiveFaxModel();
        model = model.set("DateFrom", dateFrom);
        store.setActiveFaxModel(model);
    }

    private toDateChanged(request: LocalEvent, store: PortfolioStore): void {
        let dateTo = request.Get<Date>("value");
        dateTo = dateTo && moment(dateTo).endOf("day").toDate();

        let model = store.getActiveFaxModel();
        model = model.set("DateTo", dateTo);
        store.setActiveFaxModel(model);
    }

    private onOkClick(request: LocalEvent, store: PortfolioStore): void {
        const isSaveSettingsChecked = request.Get<boolean>("isSaveSettingsChecked");
        let model = store.getActiveFaxModel();

        if (isSaveSettingsChecked)
            this.saveUserSettings(model, store);

        model = model
            .set("ShowMySentFaxDisplaySettings", false)
            .set("CurrentPage", 1)
            .set("SelectedPageNumber", 1);
        store.setActiveFaxModel(model);
        store.PortfolioActions.LoadPortfolioFaxes(model);
    }

    private async saveUserSettings(model: ISentFaxInboxModel, store: PortfolioStore): Promise<void> {
        const userSettings = new UserSettings();

        if (store.Model.get("CurrentInbox") === PortfolioInboxes.Faxes)
            userSettings.PortfolioSentFaxSearchSettings = SentFaxSearchSettings.fromModel(model);
        else
            userSettings.PortfolioFailedFaxesFilterSetting = SentFaxSearchSettings.fromModel(model);

        const requestData = {
            UserSettings: userSettings,
            UserId: sessionStore.userId
        };

        const result = await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            requestData
        );

        if (result.isSuccess)
            cacheStore.UserSettings.PortfolioSentFaxSearchSettings = userSettings.PortfolioSentFaxSearchSettings;
    }

    private onCancelClick(request: LocalEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();
        model = model.set("ShowMySentFaxDisplaySettings", false);
        store.setActiveFaxModel(model);
    }

    private onSettingsContainerCheckboxChange(request: LocalEvent, store: PortfolioStore): void {
        const target = request.Get<CheckboxTarget>("target");
        const checked = request.Get<boolean>("checked");
        const id = request.Get<string>("id");
        const isParent = request.Get<boolean>("isParent");

        let model = store.getActiveFaxModel();

        switch (target) {
            case CheckboxTarget.Myle_Portfolio_MySentFaxes_Sites:
                model = this.handleSitesCheckboxChange(model, store, id, checked);
                break;

            case CheckboxTarget.Myle_Portfolio_MySentFaxes_Clinicians:
                model = this.handleCliniciansCheckboxChange(model, store, id, checked);
                break;

            case CheckboxTarget.Myle_Portfolio_MySentFaxes_SitesControlCheck:
                model = this.handleSitesControlCheck(model, store, id, checked, isParent);
                break;

            case CheckboxTarget.Myle_Portfolio_MySentFaxes_CliniciansControlCheck:
                model = this.handleCliniciansControlCheck(model, store, id, checked, isParent);
                break;

            case CheckboxTarget.Myle_Portfolio_MySentFaxes_Status:
                model = this.handleStatusCheckboxChange(model, store, id, checked, isParent);
                break;

            case CheckboxTarget.Myle_Portfolio_MySentFaxes_IncludeIgnoredCheckboxChange:
                model = this.handleIncludeIgnoredChange(model, store, checked);
                break;

            case CheckboxTarget.Myle_Portfolio_MySentFaxes_OnlySelectedSites:
                model = model.set("ShowOnlySelectedSites", checked);
                break;

            default:
                break;
        }

        store.setActiveFaxModel(model);
    }

    private handleSitesCheckboxChange(model: ISentFaxInboxModel, store: PortfolioStore, id: string, checked: boolean): ISentFaxInboxModel {
        return model
            .set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(model.get("Sites"), id, checked))
            .set("CliniciansFiltered", store.getMySentFaxCliniciansBasedOnSitesAndQuery(model));
    }

    private handleCliniciansCheckboxChange(model: ISentFaxInboxModel, store: PortfolioStore, id: string, checked: boolean): ISentFaxInboxModel {
        return model
            .set("CliniciansGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(model.get("CliniciansGlobalCache"), id, checked))
            .set("CliniciansFiltered", store.getMySentFaxCliniciansBasedOnSitesAndQuery(model));
    }

    private handleSitesControlCheck(model: ISentFaxInboxModel, store: PortfolioStore, id: string, checked: boolean, isParent: boolean): ISentFaxInboxModel {
        let sites = model.get("Sites");
        sites = isParent
            ? store.helper.ChangeCheckStateFromAllICheckable(sites, checked)
            : store.helper.FindUpdateICheckableItem(sites, id, checked);

        const sitesState = IndeterminateCheckboxState.fromItems(sites);

        return model
            .set("Sites", sites)
            .set("SelectAllSitesChecked", sitesState.parentChecked)
            .set("SelectAllSitesIndeterminate", sitesState.parentIndeterminate)
            .set("CliniciansFiltered", store.getMySentFaxCliniciansBasedOnSitesAndQuery(model));
    }

    private handleCliniciansControlCheck(model: ISentFaxInboxModel, store: PortfolioStore, id: string, checked: boolean, isParent: boolean): ISentFaxInboxModel {
        const list = store.getMySentFaxCliniciansBasedOnSitesAndQuery(model);
        let updatedList: Immutable.List<EmployeeUIModel>;
        let updatedCache: Immutable.List<EmployeeUIModel>;
        let isChecked: boolean;

        if (isParent) {
            const listOfClinicianIds = list.toArray().map(i => i.Id);
            updatedCache = store.helper.FindUpdateICheckableItems(model.get("CliniciansGlobalCache"), listOfClinicianIds, checked);
            model = model.set("CliniciansGlobalCache", updatedCache);

            updatedList = store.getMySentFaxCliniciansBasedOnSitesAndQuery(model);
        }
        else {
            updatedCache = store.helper.FindUpdateICheckableItems(model.get("CliniciansGlobalCache"), [id], checked);
            model = model.set("CliniciansGlobalCache", updatedCache);

            updatedList = store.helper.FindUpdateICheckableItem(list, id, checked);
        }

        const cliniciansState = IndeterminateCheckboxState.fromItems(updatedList);
        isChecked = isParent ? checked : cliniciansState.parentChecked;

        return model
            .set("CliniciansFiltered", updatedList)
            .set("SelectAllCliniciansChecked", isChecked)
            .set("SelectAllCliniciansIndeterminate", cliniciansState.parentIndeterminate);
    }

    private handleStatusCheckboxChange(model: ISentFaxInboxModel, store: PortfolioStore, id: string, checked: boolean, isParent: boolean): ISentFaxInboxModel {
        const isFailedFaxView = store.Model.get("CurrentInbox") === PortfolioInboxes.FailedFaxes;
        let status = model.get("Status");

        if (isParent) {
            status = store.helper.ChangeCheckStateFromAllICheckable(status, checked);
            model = model.set("Status", status);

            if (isFailedFaxView) {
                model = model.set("IncludeIgnored", checked);
            }
        }
        else {
            status = store.helper.FindUpdateICheckableItem(status, id, checked);

            model = model.set("Status", status);
        }

        const checkableItems = status.map(s => ({ Checked: s.Checked }));

        const allItems = isFailedFaxView
            ? checkableItems.concat({ Checked: model.get("IncludeIgnored") })
            : status;

        const state = IndeterminateCheckboxState.fromItems(allItems as Immutable.List<ICheckable>);
        return model
            .set("SelectAllStatusChecked", state.parentChecked)
            .set("SelectAllStatusIndeterminate", state.parentIndeterminate);
    }

    private handleIncludeIgnoredChange(model: ISentFaxInboxModel, store: PortfolioStore, checked: boolean): ISentFaxInboxModel {
        const isFailedFaxView = store.Model.get("CurrentInbox") === PortfolioInboxes.FailedFaxes;
        const status = model.get("Status");

        model = model.set("IncludeIgnored", checked);

        const allItems = isFailedFaxView
            ? status.push({ Checked: checked } as CheckboxUIModel)
            : status;

        const state = IndeterminateCheckboxState.fromItems(allItems);
        return model
            .set("SelectAllStatusChecked", state.parentChecked)
            .set("SelectAllStatusIndeterminate", state.parentIndeterminate);
    }

    private showColumnOrderingPopup(request: LocalEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();
        model = model
            .set("ColumnOrderingPopupOpen", true)
            .set("ShowMySentFaxDisplaySettings", false);

        store.setActiveFaxModel(model);
        sessionStore.showGlobalOverlay(true);
    }

    private closeColumnOrderingPopup(request: LocalEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();
        model = model
            .set("ColumnOrderingPopupOpen", false)
            .set("ShowMySentFaxDisplaySettings", false);
        store.setActiveFaxModel(model);
        sessionStore.hideGlobalOverlay();
    }

    private closeSearchFilterPopup(request: LocalEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();
        if (model.get("ShowMySentFaxDisplaySettings") === true) {
            this.onOkClick(request, store);
            store.EmitChangeToListeners();
        }
    }
}

export { MySentFaxSettingsContainerHandler };
